import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { usePlayback } from "@/lib/playback";
import { PlaylistPlayer } from "@/components/playlist/PlaylistPlayer";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { ReactionsTicker } from "@/components/playlist/ReactionsTicker";
import Navigation from "@/components/Navigation";
import { useStore } from "@/lib/store";
import type { Submission, Round } from "@/lib/mock-data";
import { ScrollArea } from "@/components/ui/scroll-area";
import * as api from "@/lib/api";
import { useIsMobile } from "@/hooks/use-mobile";

const PlaylistPlayerPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { state: playbackState, currentTrack, playTrack } = usePlayback();
  const { state: storeState, addSubmissionsToState, addVotesToState, updateRound } = useStore();
  const [isLoadingSubmissions, setIsLoadingSubmissions] = useState(false);

  const playlistTracks = playbackState.tracks || [];
  const playlistLabel = playbackState.playlistLabel;
  
  // Check if tracks are from archive (have synthetic IDs with multiple dashes)
  const isArchiveData = useMemo(() => {
    return playlistTracks.some((track) => {
      // Archive IDs are like: "round_id-submitter_id-song_title-index"
      // Live IDs are UUIDs (format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx = 5 parts)
      // Archive IDs have more parts because they include song titles which can contain dashes
      const parts = track.submissionId.split('-');
      // UUIDs have exactly 5 parts, archive IDs have more (typically 6+)
      return parts.length > 5;
    });
  }, [playlistTracks]);
  
  // Fetch submissions for rounds in the playlist if not already loaded
  useEffect(() => {
    if (isArchiveData || playlistTracks.length === 0) return;
    
    const isSupabaseConfigured = () => {
      const url = import.meta.env.VITE_SUPABASE_URL;
      const key = import.meta.env.VITE_SUPABASE_ANON_KEY;
      return !!(url && key && url !== 'https://placeholder.supabase.co' && key !== 'placeholder-key');
    };
    
    if (!isSupabaseConfigured()) return;
    
    const roundIds = new Set(playlistTracks.map(t => t.roundId).filter(Boolean));
    const missingSubmissionsRoundIds = Array.from(roundIds).filter(roundId => {
      const hasSubmissions = storeState.submissions.some(s => s.round_id === roundId);
      return !hasSubmissions;
    });
    const missingVotesRoundIds = Array.from(roundIds).filter(roundId => {
      const hasVotes = storeState.votes.some(v => v.round_id === roundId);
      return !hasVotes;
    });
    
    const roundsToFetch = new Set([...missingSubmissionsRoundIds, ...missingVotesRoundIds]);
    
    if (roundsToFetch.size === 0) return;
    
    const fetchSubmissions = async () => {
      setIsLoadingSubmissions(true);
      try {
        for (const roundId of roundsToFetch) {
          // Fetch submissions if missing
          if (missingSubmissionsRoundIds.includes(roundId)) {
            const submissions = await api.submissionAPI.getSubmissionsByRound(roundId);
            if (submissions && submissions.length > 0) {
              addSubmissionsToState(submissions);
            }
          }
          
          // Fetch votes (which contain comments) if missing
          if (missingVotesRoundIds.includes(roundId)) {
            const votes = await api.voteAPI.getVotesByRound(roundId);
            if (votes && votes.length > 0) {
              addVotesToState(votes);
            }
          }
        }
      } catch (error) {
        console.error('[PlaylistPlayer] Failed to fetch submissions/votes:', error);
      } finally {
        setIsLoadingSubmissions(false);
      }
    };
    
    fetchSubmissions();
  }, [playlistTracks, isArchiveData, storeState.submissions, storeState.votes, addSubmissionsToState, addVotesToState]);

  // Get all submissions and rounds for lookup (only for live data)
  const submissionsMap = useMemo(() => {
    if (isArchiveData) return new Map<string, Submission>();
    const map = new Map<string, Submission>();
    storeState.submissions.forEach((submission) => {
      map.set(submission.id, submission);
    });
    
    // Also try to get submissions by round if we have tracks
    if (playlistTracks.length > 0 && !isArchiveData) {
      const roundIds = new Set(playlistTracks.map(t => t.roundId).filter(Boolean));
      roundIds.forEach(roundId => {
        const roundSubmissions = storeState.submissions.filter(s => s.round_id === roundId);
        roundSubmissions.forEach((submission) => {
          if (!map.has(submission.id)) {
            map.set(submission.id, submission);
          }
        });
      });
    }
    
    return map;
  }, [storeState.submissions, isArchiveData, playlistTracks]);

  // Fetch rounds for playlist tracks if not already loaded
  const [isLoadingRounds, setIsLoadingRounds] = useState(false);
  useEffect(() => {
    if (isArchiveData || playlistTracks.length === 0) return;
    
    const isSupabaseConfigured = () => {
      const url = import.meta.env.VITE_SUPABASE_URL;
      const key = import.meta.env.VITE_SUPABASE_ANON_KEY;
      return !!(url && key && url !== 'https://placeholder.supabase.co' && key !== 'placeholder-key');
    };
    
    if (!isSupabaseConfigured()) return;
    
    const roundIds = new Set(playlistTracks.map(t => t.roundId).filter(Boolean));
    const missingRoundIds = Array.from(roundIds).filter(roundId => {
      return !storeState.rounds.some(r => r.id === roundId);
    });
    
    if (missingRoundIds.length === 0) return;
    
    const fetchRounds = async () => {
      setIsLoadingRounds(true);
      try {
        for (const roundId of missingRoundIds) {
          const round = await api.roundAPI.getRound(roundId);
          if (round) {
            // Add round to store using updateRound (which will add if not exists)
            // Pass the full round object so it gets added if missing
            updateRound(round.id, round);
          }
        }
      } catch (error) {
        console.error('[PlaylistPlayer] Failed to fetch rounds:', error);
      } finally {
        setIsLoadingRounds(false);
      }
    };
    
    fetchRounds();
  }, [playlistTracks, isArchiveData, storeState.rounds]);

  const roundsMap = useMemo(() => {
    const map = new Map<string, Round>();
    storeState.rounds.forEach((round) => {
      map.set(round.id, round);
    });
    return map;
  }, [storeState.rounds]);

  // Get votes for all submissions in the playlist (only for live data)
  const votesBySubmission = useMemo(() => {
    if (isArchiveData) return new Map<string, typeof storeState.votes>();
    const votesMap = new Map<string, typeof storeState.votes>();
    playlistTracks.forEach((track) => {
      const submissionId = track.submissionId;
      const votes = storeState.votes.filter((vote) => vote.submission_id === submissionId);
      if (votes.length > 0) {
        votesMap.set(submissionId, votes);
      }
    });
    return votesMap;
  }, [playlistTracks, storeState.votes, isArchiveData]);

  // Enrich tracks with reactions from votes (for live data) or use existing reactions (for archive)
  const enrichedTracks = useMemo(() => {
    return playlistTracks.map((track) => {
      // For archive data, reactions are already parsed and included in track.reactions
      if (isArchiveData) {
        return track; // Archive tracks already have reactions from the JSON parsing
      }
      
      // For live data, convert votes to reactions
      const votes = votesBySubmission.get(track.submissionId) || [];
      const reactions = votes.map((vote) => {
        // Use username from vote object (fetched with join), fallback to storeState.users, then "Unknown"
        const username = vote.username || storeState.users.find((u) => u.id === vote.voter_id)?.username || "Unknown";
        return {
          id: vote.id,
          username: username,
          comment: vote.comment || "",
          points: vote.points,
          created_at: vote.created_at,
        };
      });

      return {
        ...track,
        reactions: reactions.length > 0 ? reactions : track.reactions,
      };
    });
  }, [playlistTracks, votesBySubmission, storeState.users, isArchiveData]);

  useEffect(() => {
    // Only navigate away if we've given the state time to update (check after initial mount)
    const timer = setTimeout(() => {
      if (playlistTracks.length === 0) {
        toast({
          title: "No playlist loaded",
          description: "Please select a playlist and click Play to start.",
          variant: "destructive",
        });
        navigate("/playlists");
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [playlistTracks.length, navigate, toast]);

  const handlePlayTrack = (submissionId: string) => {
    playTrack(submissionId);
  };

  if (playlistTracks.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  // Get current track's submission data
  const currentSubmission = currentTrack
    ? submissionsMap.get(currentTrack.submissionId)
    : null;
  const currentRound = currentTrack
    ? roundsMap.get(currentTrack.roundId)
    : null;
  
  // Debug logging for season display
  useEffect(() => {
    if (currentRound && currentTrack) {
      console.log('[PlaylistPlayer] Current round season info:', {
        roundId: currentRound.id,
        theme: currentRound.theme,
        season: currentRound.season,
        roundNumber: currentRound.round_number,
        trackRoundId: currentTrack.roundId
      });
    }
  }, [currentRound, currentTrack]);

  // For archive data, extract info from track itself
  const currentTrackData = useMemo(() => {
    if (!currentTrack) return null;
    
    if (isArchiveData) {
      // For archive data, use the track's data directly
      // Try to get submission from map first (if we fetched it), otherwise use track data
      const submission = submissionsMap.get(currentTrack.submissionId);
      if (submission) {
        return submission;
      }
      
      // Fallback: create a synthetic submission object from track data
      return {
        id: currentTrack.submissionId,
        song_title: currentTrack.title,
        artist_name: currentTrack.artist,
        total_points: (currentTrack as any).score || 0,
        submitter_comment: currentTrack.comment,
        fun_facts: currentTrack.fun_facts,
        round_id: currentTrack.roundId,
        user_id: '', // Not available in track
      };
    }
    
    return currentSubmission;
  }, [currentTrack, isArchiveData, currentSubmission, submissionsMap]);

  // Calculate rank for current submission (only for live data)
  const currentRank = useMemo(() => {
    if (isArchiveData) {
      // For archive, we'd need to parse from the track data or calculate from playlist
      // For now, return null or try to extract from track
      return null;
    }
    if (!currentSubmission || !currentRound) return null;
    const roundSubmissions = storeState.submissions
      .filter((s) => s.round_id === currentRound.id)
      .sort((a, b) => b.total_points - a.total_points);
    const rankIndex = roundSubmissions.findIndex((s) => s.id === currentSubmission.id);
    return rankIndex >= 0 ? rankIndex + 1 : null;
  }, [currentSubmission, currentRound, storeState.submissions, isArchiveData]);

  // Get current track with reactions
  const currentTrackWithReactions = currentTrack
    ? enrichedTracks.find((t) => t.submissionId === currentTrack.submissionId) || currentTrack
    : null;

  // Debug logging (remove in production)
  useEffect(() => {
    if (currentTrack) {
      const submissionIdParts = currentTrack.submissionId.split('-');
      console.log('[PlaylistPlayer] Current track:', {
        submissionId: currentTrack.submissionId,
        submissionIdParts: submissionIdParts.length,
        roundId: currentTrack.roundId,
        isArchiveData,
        hasSubmission: !!currentSubmission,
        submissionFunFacts: currentSubmission?.fun_facts?.length || 0,
        hasReactions: !!currentTrackWithReactions?.reactions?.length,
        reactionsCount: currentTrackWithReactions?.reactions?.length || 0,
        votesCount: votesBySubmission.get(currentTrack.submissionId)?.length || 0,
        submissionsMapSize: submissionsMap.size,
        storeSubmissionsCount: storeState.submissions.length,
      });
    }
  }, [currentTrack, isArchiveData, currentSubmission, currentTrackWithReactions, votesBySubmission, submissionsMap, storeState.submissions]);

  // Render playlist sidebar component
  const renderPlaylistSidebar = (className?: string, scrollable: boolean = false, hideHeader: boolean = false) => {
    const playlistContent = (
      <div className="grid gap-3">
                    {enrichedTracks.map((track, index) => {
                      const submission = submissionsMap.get(track.submissionId);
                      const isActive = currentTrack?.submissionId === track.submissionId;
                      const votes = votesBySubmission.get(track.submissionId) || [];
                      const totalPoints = isArchiveData 
                        ? (track as any).score || 0
                        : (submission?.total_points || 0);
                      
                      return (
                        <button
                          key={track.submissionId}
                          type="button"
                          onClick={() => handlePlayTrack(track.submissionId)}
                          className={cn(
                            "flex flex-col gap-2 rounded-2xl border px-5 py-4 text-left text-base transition-colors sm:flex-row sm:items-center sm:justify-between sm:text-lg",
                            isActive
                              ? "border-primary/70 bg-primary/15 text-primary"
                              : "border-border bg-background/70 hover:border-primary/40 hover:bg-muted/50",
                          )}
                        >
                          <div className="flex items-center gap-4">
                            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-muted text-base font-semibold sm:text-lg">
                              {index + 1}
                            </span>
                            <div>
                              <p className="text-base font-semibold text-foreground sm:text-lg">{track.title}</p>
                              <p className="text-sm text-muted-foreground sm:text-base">
                                {track.artist} • Submitted by {track.submitter}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4 text-sm sm:text-base">
                            <span className="font-semibold text-primary">{Math.round(totalPoints)} pts</span>
                            {votes.length > 0 && (
                              <span className="text-muted-foreground">{votes.length} vote{votes.length === 1 ? '' : 's'}</span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
    );

    return (
      <Card className={cn("border-muted/40 bg-card/40 shadow-lg shadow-primary/10 flex flex-col", scrollable ? "min-h-0 overflow-hidden" : "h-full", className)}>
        <CardContent className={cn(
          scrollable 
            ? hideHeader 
              ? "flex flex-col min-h-0 flex-1 overflow-hidden p-0" 
              : "flex flex-col min-h-0 flex-1 overflow-hidden p-0"
            : hideHeader
              ? "p-6 sm:p-7"
              : "p-6 sm:p-7"
        )}>
          {scrollable ? (
            <>
              {!hideHeader && (
                <div className="px-6 pt-6 pb-4 sm:px-7 sm:pt-7 sm:pb-5 shrink-0 border-b border-border/50">
                  <h2 className="text-base font-semibold uppercase tracking-wider text-muted-foreground sm:text-lg">
                    Browse Playlist
                  </h2>
                </div>
              )}
              <ScrollArea className="flex-1 min-h-0">
                <div className={cn("sm:px-7 sm:py-5", hideHeader ? "px-6 py-6" : "px-6 py-4")}>
                  {playlistContent}
                </div>
              </ScrollArea>
            </>
          ) : (
            <>
              {!hideHeader && (
                <h2 className="text-base font-semibold uppercase tracking-wider text-muted-foreground sm:text-lg">
                  Browse Playlist
                </h2>
              )}
              <div className={hideHeader ? "" : "mt-5"}>
                {playlistContent}
              </div>
            </>
          )}
                </CardContent>
              </Card>
    );
  };

  // Render song info component
  const renderSongInfo = (className?: string) => {
    if (!currentTrackData || !currentTrack) return null;
    return (
      <Card className={cn("border-muted/60 bg-card/50 shadow-lg shadow-primary/10", className)}>
                  <CardContent className="space-y-5 p-5 sm:p-6">
                    <div>
                      <p className="text-sm font-semibold uppercase tracking-[0.3em] text-purple-300/80">
                        {currentRound?.season || "Season 1"}
                      </p>
                      <h2 className="mt-2 text-2xl font-bold text-foreground sm:text-3xl">
                        {currentRound 
                          ? currentRound.theme
                          : (currentTrack as any)?.roundName || currentTrack.title}
                      </h2>
                    </div>
                    <div className="space-y-3 rounded-2xl border border-muted bg-background/80 p-5 text-base leading-7 text-foreground sm:text-lg">
                      <p>
                        <span className="font-medium text-primary">Artist:</span> {currentTrackData.artist_name || currentTrack.artist}
                      </p>
                      <p>
                        <span className="font-medium text-primary">Song:</span> {currentTrackData.song_title || currentTrack.title}
                      </p>
                      <p>
                        <span className="font-medium text-primary">Submitter:</span> {currentTrack.submitter || "Unknown"}
                      </p>
                      <p>
                        <span className="font-medium text-primary">Score:</span> {
                          isArchiveData 
                            ? Math.round((playlistTracks.find(t => t.submissionId === currentTrack.submissionId) as any)?.score || 0)
                            : Math.round(currentTrackData.total_points || 0)
                        }
                      </p>
                      {currentRank && (
                        <p>
                          <span className="font-medium text-primary">Rank:</span> {currentRank}
                        </p>
                      )}
                      {(currentTrackData.submitter_comment || currentTrack.comment) && (
                        <p>
                          <span className="font-medium text-primary">Comment:</span> {currentTrackData.submitter_comment || currentTrack.comment}
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
    );
  };

  // Render comments component
  const renderComments = (className?: string) => {
    if (!currentTrack) return null;
    
    // Get fun facts from submission or track (for archive, track has the data)
    // Priority: currentSubmission > currentTrackData > currentTrack > currentTrackWithReactions
    const funFacts = currentSubmission?.fun_facts 
      || currentTrackData?.fun_facts 
      || currentTrack?.fun_facts
      || (currentTrackWithReactions as any)?.fun_facts;
    
    // Get reactions - they should be on the track
    // For archive data, reactions are parsed from comments and included in track.reactions
    let reactions = currentTrackWithReactions?.reactions 
      || currentTrack?.reactions 
      || [];
    
    // If no reactions found and we have archiveEntry, parse comments from archiveEntry
    if (isArchiveData && reactions.length === 0 && (currentTrack as any).archiveEntry) {
      const archiveEntry = (currentTrack as any).archiveEntry;
      if (archiveEntry.comments) {
        console.log('[PlaylistPlayer] 🔄 Parsing reactions from archiveEntry.comments:', {
          commentsLength: archiveEntry.comments.length,
          commentsPreview: archiveEntry.comments.substring(0, 100),
        });
        const segments = archiveEntry.comments.split("|");
        reactions = segments
          .map((segment: string, index: number) => {
            const trimmed = segment.trim();
            if (!trimmed) {
              return null;
            }
            
            const match = trimmed.match(/^\s*([^:]+):\s*([-+]?\d+(?:\.\d+)?)\s*:\s*(.*)$/);
            if (match) {
              const [, usernameRaw, pointsRaw, commentRaw] = match;
              return {
                id: `${archiveEntry.round_id}-${archiveEntry.submitter_id}-comment-${index}`,
                username: usernameRaw.trim(),
                points: Number(pointsRaw),
                comment: commentRaw.trim(),
              };
            } else {
              return {
                id: `${archiveEntry.round_id}-${archiveEntry.submitter_id}-comment-${index}`,
                username: "Judge",
                points: 0,
                comment: trimmed,
              };
            }
          })
          .filter((r: any) => r !== null);
        console.log('[PlaylistPlayer] ✅ Parsed', reactions.length, 'reactions from archiveEntry');
      }
    }
    
    // Debug logging for data source
    if (currentTrack) {
      console.log('[PlaylistPlayer] 📊 Current track data source:', {
        isArchive: isArchiveData,
        trackId: currentTrack.submissionId,
        song: currentTrack.title,
        hasSubmission: !!currentSubmission,
        submissionFunFacts: currentSubmission?.fun_facts?.length || 0,
        trackDataFunFacts: currentTrackData?.fun_facts?.length || 0,
        trackFunFacts: currentTrack?.fun_facts?.length || 0,
        enrichedTrackFunFacts: (currentTrackWithReactions as any)?.fun_facts?.length || 0,
        finalFunFacts: funFacts?.length || 0,
        hasComments: reactions.length > 0,
        commentsCount: reactions.length,
        trackReactions: currentTrack?.reactions?.length || 0,
        trackWithReactionsReactions: currentTrackWithReactions?.reactions?.length || 0,
        hasArchiveEntry: !!(currentTrack as any).archiveEntry,
        archiveEntryComments: (currentTrack as any).archiveEntry?.comments?.substring(0, 50) || 'none',
        archiveEntryFunFacts: (currentTrack as any).archiveEntry?.fun_facts?.length || 0,
      });
    }
    
    return (
      <Card className={cn("border-muted/60 bg-card/50 shadow-lg shadow-primary/10 h-full", className)}>
                  <CardContent className="p-5 sm:p-6">
                    <div data-tour="reactions-ticker">
                      <ReactionsTicker
                        roundId={currentTrack.roundId}
                        submissionId={currentTrack.submissionId}
                        isActive={playbackState.isPlaying}
                        staticReactions={reactions}
                        variant="spotlight"
                        submitterComment={currentTrackData?.submitter_comment || currentTrack.comment}
                        isArchive={isArchiveData}
                        roundStatus={currentRound?.status}
                        funFacts={funFacts || undefined}
                      />
                    </div>
                  </CardContent>
                </Card>
    );
  };

  return (
    <div className="min-h-screen bg-background" data-tour="playlist-player" style={{ paddingTop: '64px' }}>
      <Navigation />
      <main className="mx-auto flex w-full flex-col gap-8 px-3 pt-3 pb-6 sm:px-6 lg:px-10 lg:min-h-0 lg:pt-4">
        <div className="rounded-lg border border-primary/20 bg-primary/5 px-4 py-3 text-sm text-muted-foreground">
          YouTube Autoplay is limited by mobile browser policies. For the smoothest playback, use a laptop or desktop.
        </div>
        <div className="w-full space-y-4 lg:min-h-0">
          {/* 60/40 Split Layout - Video and Playlist on top, Song Info and Comments below */}
          <PlaylistLayout
            renderPlaylistSidebar={renderPlaylistSidebar}
            renderSongInfo={renderSongInfo}
            renderComments={renderComments}
            currentTrack={currentTrack}
          />
        </div>
      </main>
    </div>
  );
};

// Component to match playlist height to video height (60/40 split layout)
const PlaylistLayout = ({
  renderPlaylistSidebar,
  renderSongInfo,
  renderComments,
  currentTrack,
}: {
  renderPlaylistSidebar: (className?: string, scrollable?: boolean, hideHeader?: boolean) => React.ReactElement;
  renderSongInfo: (className?: string) => React.ReactElement | null;
  renderComments: (className?: string) => React.ReactElement | null;
  currentTrack: any;
}) => {
  const isMobile = useIsMobile();
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const [playlistHeight, setPlaylistHeight] = useState<number>(400); // Default fallback

  useEffect(() => {
    const updateHeight = () => {
      if (videoContainerRef.current) {
        // Measure the actual rendered height of the video container
        const height = videoContainerRef.current.getBoundingClientRect().height;
        if (height > 0) {
          setPlaylistHeight((prev) => {
            // Only update if height actually changed (avoid unnecessary re-renders)
            return Math.abs(prev - height) > 1 ? height : prev;
          });
        }
      }
    };

    // Initial measurement
    const timeoutId1 = setTimeout(updateHeight, 100);
    const timeoutId2 = setTimeout(updateHeight, 500); // Second check after video loads
    
    // Use ResizeObserver to watch for size changes
    let resizeObserver: ResizeObserver | null = null;
    if (videoContainerRef.current && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updateHeight);
      resizeObserver.observe(videoContainerRef.current);
    }

    // Also listen for window resize
    window.addEventListener('resize', updateHeight);

    return () => {
      clearTimeout(timeoutId1);
      clearTimeout(timeoutId2);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener('resize', updateHeight);
    };
  }, [currentTrack]); // Only re-run when track changes

  return (
    <>
      {/* Mobile layout: Video -> Comments -> Song Meta -> Queue */}
      {isMobile && (
      <div className="space-y-6">
        <div ref={videoContainerRef}>
          <div className="[&>div>div:first-child]:!hidden [&>div>div:last-child]:!pt-0">
            <PlaylistPlayer
              layout="focused"
              hideQueue
              showTicker={false}
              showTrackDetails={false}
              videoHeight="auto"
              videoMaxHeight={420}
              className="overflow-hidden rounded-3xl border border-primary/30 bg-gradient-to-br from-primary/15 via-card to-card shadow-[0_45px_120px_-40px_rgba(99,102,241,0.55)]"
            />
          </div>
        </div>

        {/* Comments ticker */}
        {renderComments()}

        {/* Song meta */}
        {renderSongInfo()}

        {/* Queue */}
        <div className="max-h-[60vh] overflow-hidden">
          {renderPlaylistSidebar(undefined, true, false)}
        </div>
      </div>
      )}

      {/* Desktop layout: 60/40 split with matched heights */}
      {!isMobile && (
      <div>
        {/* Top row: Video and Playlist - matching heights */}
        <div className="grid w-full grid-cols-5 gap-8">
          {/* Video column (60%) */}
          <div className="col-span-5 lg:col-span-3" ref={videoContainerRef}>
            <div className="[&>div>div:first-child]:!hidden [&>div>div:last-child]:!pt-0">
              <PlaylistPlayer
                layout="focused"
                hideQueue
                showTicker={false}
                showTrackDetails={false}
                videoHeight="auto"
                videoMaxHeight={400}
                className="overflow-hidden rounded-3xl border border-primary/30 bg-gradient-to-br from-primary/15 via-card to-card shadow-[0_45px_120px_-40px_rgba(99,102,241,0.55)]"
              />
            </div>
          </div>
          
          {/* Playlist column (40%) - matches video height */}
          <div className="col-span-5 lg:col-span-2">
            <div 
              className="w-full overflow-hidden"
              style={{ height: `${playlistHeight}px` }}
            >
              {renderPlaylistSidebar("h-full", true, true)}
            </div>
          </div>
        </div>
        
        {/* Bottom row: Song Info and Comments */}
        <div className="grid w-full grid-cols-5 gap-8 lg:items-start mt-4">
          {/* Song Info directly below video */}
          <div className="col-span-5 lg:col-span-1">
            {renderSongInfo()}
          </div>
          {/* Comments directly below playlist */}
          <div className="col-span-5 lg:col-span-4">
            {renderComments()}
          </div>
        </div>
      </div>
      )}
    </>
  );
};

export default PlaylistPlayerPage;
